import { APIS } from "@/apis";
import CONFIGS from "@/core/utils/configs";
import CONSTANTS from "@/core/utils/constants";
import {
  getCookies,
  getLocalStorage,
  setCookies,
  setLocalStorage,
} from "@/core/utils/helpers";
import { app, authentication } from "@microsoft/teams-js";
import { reportTelemetryError, reportTelemetryFailure } from "@/core/telemetry";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import { useStore } from "@/store/store";
import type { CompanyWithDomain } from "@/hooks/useDomainSelector.types";

export type { CompanyWithDomain } from "@/hooks/useDomainSelector.types";

const useDomainSelector = (tokenReady: boolean) => {
  const { t } = useTranslation();
  const cachedDomain = getCookies(CONSTANTS.COOKIES.DOMAIN);
  const cachedCompanyId = getLocalStorage(CONSTANTS.PLATFORM);
  const shouldAutoSelect =
    CONFIGS.ENVIRONMENT !== "prod" || !!(cachedDomain && cachedCompanyId);
  const [loading, setLoading] = useState(!shouldAutoSelect);
  const [consentRequired, setConsentRequired] = useState(false);
  const [error, setError] = useState<string>("");
  const [noWorkspace, setNoWorkspace] = useState(false);
  const [authExpired, setAuthExpired] = useState(false);
  const [companies, setCompanies] = useState<CompanyWithDomain[]>([]);
  const silentAuthRetryCount = useRef(0);
  const consentPropagationRetryCount = useRef(0);
  const updateApiUrl = useStore((state) => state.updateApiUrl);
  const updateConfig = useStore((state) => state.updateConfig);
  const updateUsername = useStore((state) => state.updateUsername);
  const companyId = useStore((state) => state.config.companyId);
  const companyApiDomain = useStore((state) => state.config.api_end_point);

  const selectCompany = useCallback(
    (company: CompanyWithDomain) => {
      updateApiUrl(company.apiDomain);
      const expires = new Date(
        new Date().getTime() + CONSTANTS.COOKIES_LONG_DURATION,
      );
      setCookies(CONSTANTS.COOKIES.DOMAIN, expires, company.apiDomain);
      setLocalStorage(CONSTANTS.PLATFORM, `${company.id}`);
      if (company.username) {
        updateUsername(company.username);
      }
      updateConfig(company);
    },
    [updateApiUrl, updateConfig, updateUsername],
  );

  const requestConsent = useCallback(async () => {
    try {
      const inTeams = await app
        .initialize()
        .then(() => true)
        .catch(() => false);

      if (inTeams) {
        const newToken = await authentication.authenticate({
          url: `${window.location.origin}/auth-start`,
          width: 600,
          height: 535,
        });
        setLocalStorage(CONSTANTS.MICROSOFT_GRAPH_TOKEN, newToken);
        sessionStorage.setItem(CONSTANTS.CONSENT_JUST_GRANTED, "1");
        setConsentRequired(false);
        window.location.reload();
      } else {
        window.location.href = `${window.location.origin}/auth-start`;
      }
    } catch (authErr) {
      console.warn("Auth popup failed:", authErr);
    }
  }, []);

  const fetchCompanies = useCallback(async () => {
    setLoading(true);
    setError("");
    setNoWorkspace(false);

    try {
      const token = getLocalStorage(CONSTANTS.MICROSOFT_GRAPH_TOKEN) ?? "";
      const results = await Promise.allSettled(
        CONFIGS.API_DOMAINS.map((apiDomain) =>
          APIS.companies.fetch(1, 50, undefined, undefined, apiDomain, token),
        ),
      );

      const allCompanies: CompanyWithDomain[] = [];
      const errors: unknown[] = [];

      results.forEach((result, index) => {
        if (result.status === "fulfilled") {
          const apiDomain = CONFIGS.API_DOMAINS[index];
          result.value.data.forEach((c) => {
            allCompanies.push({ ...c, apiDomain });
          });
        } else {
          errors.push(result.reason);
        }
      });

      if (allCompanies.length === 0) {
        const firstError = errors[0];
        if (firstError) {
          const axiosError = firstError as AxiosError<{ error?: string }>;
          const errorCode = axiosError.response?.data?.error;

          if (errorCode === "consent_required") {
            const justGranted =
              sessionStorage.getItem(CONSTANTS.CONSENT_JUST_GRANTED) === "1";
            const retryIndex = consentPropagationRetryCount.current;
            const delay = justGranted
              ? CONSTANTS.CONSENT_PROPAGATION_RETRY_DELAYS_MS[retryIndex]
              : undefined;

            if (delay !== undefined) {
              consentPropagationRetryCount.current += 1;
              await new Promise((resolve) => setTimeout(resolve, delay));
              return fetchCompanies();
            }

            sessionStorage.removeItem(CONSTANTS.CONSENT_JUST_GRANTED);
            reportTelemetryFailure("consent_required");
            setConsentRequired(true);
            setLoading(false);
            return;
          }

          if (axiosError.response?.status === 401) {
            localStorage.removeItem(CONSTANTS.MICROSOFT_GRAPH_TOKEN);

            if (
              silentAuthRetryCount.current < CONSTANTS.MAX_SILENT_AUTH_RETRIES
            ) {
              silentAuthRetryCount.current += 1;

              const inTeams = await app
                .initialize()
                .then(() => true)
                .catch(() => false);

              if (inTeams) {
                try {
                  const newToken = await authentication.getAuthToken();
                  setLocalStorage(CONSTANTS.MICROSOFT_GRAPH_TOKEN, newToken);
                  return fetchCompanies();
                } catch {
                  // Silent token renewal failed — fall through and ask the
                  // user to sign in again explicitly instead of automatically
                  // redirecting through the interactive consent screen.
                }
              }
            }

            reportTelemetryFailure("auth_expired", {
              silentRetries: silentAuthRetryCount.current,
            });
            setAuthExpired(true);
            setLoading(false);
            return;
          }

          sessionStorage.removeItem(CONSTANTS.CONSENT_JUST_GRANTED);
          reportTelemetryFailure("companies_unavailable", {
            domains: CONFIGS.API_DOMAINS.length,
            statuses: errors.map(
              (e) => (e as AxiosError).response?.status ?? "network",
            ),
          });
          setError(t("Unable to load companies. Please try again."));
          setLoading(false);
          return;
        }

        sessionStorage.removeItem(CONSTANTS.CONSENT_JUST_GRANTED);
        reportTelemetryFailure("no_workspace", {
          domains: CONFIGS.API_DOMAINS.length,
        });
        setNoWorkspace(true);
        setLoading(false);
        return;
      }

      silentAuthRetryCount.current = 0;
      consentPropagationRetryCount.current = 0;
      sessionStorage.removeItem(CONSTANTS.CONSENT_JUST_GRANTED);

      if (allCompanies.length === 1) {
        setLoading(false);
        return selectCompany(allCompanies[0]);
      }

      setCompanies(allCompanies);
      setLoading(false);
    } catch (err) {
      reportTelemetryError(err, { stage: "companies_unavailable" });
      setError(t("Unable to load companies. Please try again."));
      setLoading(false);
    }
  }, [selectCompany, t]);

  const openExternalLink = useCallback(
    async (url: string, errorMessage: string) => {
      try {
        const inTeams = await app
          .initialize()
          .then(() => true)
          .catch(() => false);

        if (inTeams) {
          await app.openLink(url);
        } else {
          window.open(url, "_blank", "noopener,noreferrer");
        }
      } catch (err) {
        console.error(errorMessage, err);
      }
    },
    [],
  );

  const goToMarketingSite = useCallback(
    () =>
      openExternalLink(CONFIGS.MARKETING_URL, "Failed to open marketing site:"),
    [openExternalLink],
  );

  const goToHelpdesk = useCallback(
    () => openExternalLink(CONFIGS.HELPDESK_URL, "Failed to open helpdesk:"),
    [openExternalLink],
  );

  const retrySignIn = useCallback(async () => {
    try {
      const inTeams = await app
        .initialize()
        .then(() => true)
        .catch(() => false);

      if (inTeams) {
        const newToken = await authentication.authenticate({
          url: `${window.location.origin}/auth-start`,
          width: 600,
          height: 535,
        });
        setLocalStorage(CONSTANTS.MICROSOFT_GRAPH_TOKEN, newToken);
        silentAuthRetryCount.current = 0;
        setAuthExpired(false);
        return fetchCompanies();
      }

      window.location.href = `${window.location.origin}/auth-start`;
    } catch (authErr) {
      console.warn("Retry sign-in failed:", authErr);
    }
  }, [fetchCompanies]);

  useEffect(() => {
    if (!tokenReady) return;

    fetchCompanies();
  }, [tokenReady, fetchCompanies]);

  return {
    companyId,
    companyApiDomain,
    loading,
    selectCompany,
    consentRequired,
    requestConsent,
    companies,
    error,
    noWorkspace,
    authExpired,
    retrySignIn,
    retry: fetchCompanies,
    goToMarketingSite,
    goToHelpdesk,
  };
};

export default useDomainSelector;
